// Aluguel & Hospedagem (0.24): imóvel inteiro ou por cômodo, reservas (Airbnb, Booking, direto...), receitas,
// despesas, comissões, limpeza criada sozinha no check-out e os números do mês (ocupação, diária média, lucro).
import { CartaoApoio } from '../components/Recolhivel';
import { useReducer } from 'react';
import { Alert, Platform, Pressable, Text, View } from 'react-native';
import type { Reg } from '../store/types';
import type { Colecao, Ctx, ModDef, PainelProps } from './tipos';
import { dataBr, din, dinDe, dinSomas, diasEntre, junta, moedaDo, num, op, somaPorMoeda, temNum, temSoma } from './util';
import { mudarCampos, novoReg, regsDe, salvarVarios } from '../store/modulos';
import { compartilharTexto } from '../lib/anexos';
import { abrirRota, whatsapp } from '../lib/mapa';
import { semBloqueio } from '../auth/AuthContext';
import { C, alpha } from '../components/theme';
import { addDays, today, uid, horaTela } from '../lib/date';
import { N_, t } from '../lib/i18n';

const STATUS = [N_('Livre'), N_('Ocupado'), N_('Uso próprio'), N_('Em limpeza'), N_('Manutenção')];
const ICONE_STATUS: Record<string, string> = { 'Livre': '🟢', 'Ocupado': '🔴', 'Uso próprio': '🏡', 'Em limpeza': '🧹', 'Manutenção': '🔧' };
const CANAIS = [N_('Airbnb'), N_('Booking'), N_('VRBO'), N_('Direto'), N_('Indicação'), N_('Outro')];
const ICONE_CANAL: Record<string, string> = { 'Airbnb': '🅰️', 'Booking': '🅱️', 'VRBO': '🏖️', 'Direto': '🤝', 'Indicação': '👥' };
const COMISSAO_PADRAO: Record<string, number> = { 'Airbnb': 3, 'Booking': 15, 'VRBO': 8 };
const CHECKLIST = [N_('Trocar roupa de cama e toalhas'), N_('Limpar banheiro'), N_('Limpar cozinha e louça'), N_('Aspirar e passar pano'),
  N_('Tirar o lixo'), N_('Repor papel higiênico, sabonete e café'), N_('Conferir controles, chaves e Wi-Fi'), N_('Fotos do espaço pronto')];

const conta = (r: Reg) => r.c.status !== 'Cancelada' && r.c.status !== 'Bloqueio';
const noites = (c: Record<string, any>) => (c.checkin && c.checkout ? Math.max(0, diasEntre(c.checkin, c.checkout)) : 0);

/** Totais da reserva: bruto (diárias + limpeza + extras), comissão da plataforma e líquido. */
function calcReserva(c: Record<string, any>) {
  const n = noites(c);
  const bruto = num(c.diaria) * n + num(c.taxaLimpeza) + num(c.extras);
  const comissao = Math.round(bruto * num(c.comissao)) / 100;
  return { n, bruto, comissao, liquido: bruto - comissao };
}

/** Noites da reserva que caem dentro do mês (AAAA-MM). */
function noitesNoMes(r: Reg, mes: string) {
  if (!r.c.checkin || !r.c.checkout) return 0;
  const ini = `${mes}-01`;
  const [a, m] = mes.split('-').map(Number);
  const fim = `${mes}-${String(new Date(a, m, 0).getDate()).padStart(2, '0')}`;
  const de = r.c.checkin > ini ? r.c.checkin : ini;
  const ate = r.c.checkout < addDays(fim, 1) ? r.c.checkout : addDays(fim, 1);
  return Math.max(0, diasEntre(de, ate));
}

/** Números do mês de um ou de todos os espaços. */
export function numerosDoMes(ctx: Ctx, mes: string, espacos: Reg[]) {
  const ids = new Set(espacos.map(e => e.id));
  const [a, m] = mes.split('-').map(Number);
  const diasMes = new Date(a, m, 0).getDate();
  const regs = regsDe(ctx.mod);
  let ocupadas = 0, bruto = 0, comissao = 0, liquido = 0, diarias = 0;
  for (const r of regs) {
    if (r.col !== 'reserva' || !ids.has(r.pai ?? '') || !conta(r)) continue;
    const nm = noitesNoMes(r, mes);
    if (!nm) continue;
    const c = calcReserva(r.c);
    const parte = c.n ? nm / c.n : 0;
    ocupadas += nm; bruto += c.bruto * parte; comissao += c.comissao * parte; liquido += c.liquido * parte; diarias += num(r.c.diaria) * nm;
  }
  const doMes = regs.filter(r => (r.col === 'despesa' || r.col === 'limpeza') && ids.has(r.pai ?? '') && String(r.c.data ?? '').startsWith(mes));
  // 0.69 (B1): despesa em outra moeda não entra na conta do lucro (não converte); aparece separada
  const naMoeda = (r: Reg) => r.col !== 'despesa' || moedaDo(r.c, 'valor', ctx.moeda) === ctx.moeda;
  const despesas = doMes.filter(naMoeda).reduce((s, r) => s + num(r.c.valor), 0);
  const despesasOutras = somaPorMoeda(doMes.filter(r => !naMoeda(r)), r => num(r.c.valor), ctx.moeda);
  const disponiveis = diasMes * Math.max(1, espacos.length);
  return { ocupadas, disponiveis, ocupacao: Math.round((ocupadas / disponiveis) * 100), bruto, comissao, liquido, despesas, lucro: liquido - despesas, despesasOutras,
    diariaMedia: ocupadas ? diarias / ocupadas : 0, revpar: bruto / disponiveis };
}

const mesAtual = () => today().slice(0, 7);
const nomeMes = (mes: string) => { const [a, m] = mes.split('-').map(Number); return new Date(a, m - 1, 1).toLocaleDateString(undefined, { month: 'long', year: 'numeric' }); };
const mudarMes = (mes: string, d: number) => { const [a, m] = mes.split('-').map(Number); const x = new Date(a, m - 1 + d, 1); return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}`; };

let mesPainel = mesAtual();
function Numeros({ ctx, espacos, titulo }: { ctx: Ctx; espacos: Reg[]; titulo: string }) {
  const [, re] = useRe();
  const n = numerosDoMes(ctx, mesPainel, espacos);
  const Bloco = ({ v, l, cor }: { v: string; l: string; cor?: string }) => (
    <View style={{ flexGrow: 1, flexBasis: '30%', backgroundColor: alpha(cor ?? ctx.cor, 0.14), borderRadius: 12, padding: 8, alignItems: 'center' }}>
      <Text style={{ color: C.text, fontWeight: '800', fontSize: 15 }} numberOfLines={1}>{v}</Text><Text style={{ color: C.sub, fontSize: 11 }}>{l}</Text>
    </View>
  );
  return (
    <View style={{ backgroundColor: C.card, borderRadius: 14, padding: 12, gap: 8 }}>
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
        <Pressable hitSlop={10} onPress={() => { mesPainel = mudarMes(mesPainel, -1); re(); }}><Text style={{ color: C.sub, fontSize: 20 }}>‹</Text></Pressable>
        <Text style={{ color: C.text, fontWeight: '800', flex: 1, textAlign: 'center' }}>📊 {titulo} · {nomeMes(mesPainel)}</Text>
        <Pressable hitSlop={10} onPress={() => { mesPainel = mudarMes(mesPainel, 1); re(); }}><Text style={{ color: C.sub, fontSize: 20 }}>›</Text></Pressable>
      </View>
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6 }}>
        <Bloco v={`${n.ocupacao}%`} l={t('ocupação')} />
        <Bloco v={din(n.diariaMedia, ctx.moeda)} l={t('diária média')} />
        <Bloco v={din(n.revpar, ctx.moeda)} l={t('por dia disponível')} />
        <Bloco v={din(n.bruto, ctx.moeda)} l={t('faturamento')} cor={C.ok} />
        <Bloco v={din(n.comissao + n.despesas, ctx.moeda)} l={t('taxas e despesas')} cor={C.danger} />
        <Bloco v={din(n.lucro, ctx.moeda)} l={t('lucro líquido')} cor={n.lucro >= 0 ? C.ok : C.danger} />
      </View>
      {temSoma(n.despesasOutras) && <Text style={{ color: C.sub, fontSize: 11 }}>{t('Despesas em outra moeda (fora do lucro): {v}', { v: dinSomas(n.despesasOutras, ctx.moeda) })}</Text>}
      <Text style={{ color: C.mute, fontSize: 11 }}>{t('{o} de {d} noites ocupadas. Reservas que passam de um mês para outro são divididas pelas noites.', { o: n.ocupadas, d: n.disponiveis })}</Text>
    </View>
  );
}
// re-render simples para o seletor de mês (sem guardar estado na ficha)
const useRe = () => { const [x, f] = useReducer((n: number) => n + 1, 0); return [x, f] as const; };

/** Check-ins, check-outs e limpezas dos próximos 7 dias. */
function Proximos({ ctx, espacos, abrir }: { ctx: Ctx; espacos: Reg[]; abrir: (r: Reg) => void }) {
  const ids = new Set(espacos.map(e => e.id));
  const hoje = today(), ate = addDays(hoje, 7);
  const ev: { d: string; h: string; txt: string; r: Reg; icone: string }[] = [];
  for (const r of regsDe(ctx.mod)) {
    if (!ids.has(r.pai ?? '')) continue;
    const esp = ctx.porId(r.pai)?.c.nome ?? '';
    if (r.col === 'reserva' && conta(r)) {
      if (r.c.checkin >= hoje && r.c.checkin <= ate) ev.push({ d: r.c.checkin, h: r.c.horaIn || '', txt: t('Check-in: {h} · {e}', { h: r.c.hospede || '?', e: esp }), r, icone: '🛬' });
      if (r.c.checkout >= hoje && r.c.checkout <= ate) ev.push({ d: r.c.checkout, h: r.c.horaOut || '', txt: t('Check-out: {h} · {e}', { h: r.c.hospede || '?', e: esp }), r, icone: '🛫' });
    }
    if (r.col === 'limpeza' && !r.c.feito && r.c.data >= hoje && r.c.data <= ate) ev.push({ d: r.c.data, h: r.c.hora || '', txt: t('Limpeza · {e}', { e: esp }) + (r.c.responsavel ? ` (${r.c.responsavel})` : ''), r, icone: '🧹' });
  }
  if (!ev.length) return null;
  ev.sort((a, b) => (a.d + a.h).localeCompare(b.d + b.h));
  return (
    <CartaoApoio titulo={'🗓️ ' + t('Próximos 7 dias')} chave={'hosp:prox7'} resumo={String(ev.length)} gap={6}>
      {ev.map((x, i) => (
        <Pressable key={i} onPress={() => abrir(x.r)} style={{ flexDirection: 'row', gap: 8, alignItems: 'center' }}>
          <Text style={{ fontSize: 16 }}>{x.icone}</Text>
          <Text style={{ color: C.sub, width: 70, fontSize: 12 }}>{dataBr(x.d).slice(0, 5)}{x.h ? ' ' + horaTela(x.h) : ''}</Text>
          <Text style={{ color: C.text, flex: 1 }} numberOfLines={1}>{x.txt}</Text>
        </Pressable>
      ))}
    </CartaoApoio>
  );
}

function PainelEspacos({ ctx, lista, abrir }: PainelProps) {
  if (!lista.length) return null;
  return <View style={{ gap: 10 }}><Numeros ctx={ctx} espacos={lista} titulo={t('Todos')} /><Proximos ctx={ctx} espacos={lista} abrir={abrir} /></View>;
}
function PainelReservas({ ctx, pai, abrir }: PainelProps) {
  if (!pai) return null;
  const temIcal = !!(pai.c.icalAirbnb || pai.c.icalBooking || pai.c.icalOutro);
  return (
    <View style={{ gap: 10 }}>
      <Numeros ctx={ctx} espacos={[pai]} titulo={pai.c.nome || t('Espaço')} />
      <Proximos ctx={ctx} espacos={[pai]} abrir={abrir} />
      {temIcal && (
        <Pressable onPress={() => importarCalendarios(ctx, pai)} style={{ backgroundColor: alpha(ctx.cor, 0.15), borderRadius: 12, padding: 10, alignItems: 'center' }}>
          <Text style={{ color: ctx.cor, fontWeight: '800' }}>🔄 {t('Importar reservas do Airbnb, Booking...')}</Text>
        </Pressable>
      )}
    </View>
  );
}

// ─── Calendário (iCal) das plataformas ───
type EvIcal = { uid: string; ini: string; fim: string; resumo: string; desc: string };
function lerIcal(txt: string): EvIcal[] {
  const linhas = txt.replace(/\r\n[ \t]/g, '').split(/\r?\n/);
  const out: EvIcal[] = [];
  let cur: Partial<EvIcal> | null = null;
  const data = (v: string) => { const m = v.match(/(\d{4})(\d{2})(\d{2})/); return m ? `${m[1]}-${m[2]}-${m[3]}` : ''; };
  for (const l of linhas) {
    if (l === 'BEGIN:VEVENT') cur = {};
    else if (l === 'END:VEVENT') { if (cur?.ini && cur.fim) out.push({ uid: cur.uid || cur.ini + cur.fim, resumo: '', desc: '', ...cur } as EvIcal); cur = null; }
    else if (cur) {
      const i = l.indexOf(':'); if (i < 0) continue;
      const k = l.slice(0, i).split(';')[0], v = l.slice(i + 1);
      if (k === 'UID') cur.uid = v; else if (k === 'DTSTART') cur.ini = data(v); else if (k === 'DTEND') cur.fim = data(v);
      else if (k === 'SUMMARY') cur.resumo = v; else if (k === 'DESCRIPTION') cur.desc = v.replace(/\\n/g, '\n');
    }
  }
  return out;
}
async function importarCalendarios(ctx: Ctx, esp: Reg) {
  const fontes: [string, string][] = [['Airbnb', esp.c.icalAirbnb], ['Booking', esp.c.icalBooking], ['Outro', esp.c.icalOutro]].filter(([, u]) => !!u && /^https?:\/\//i.test(String(u).trim())) as [string, string][];
  const existentes = ctx.filhos(esp.id, 'reserva');
  const novas: Reg[] = [];
  let lidos = 0, falhas = 0;
  for (const [canal, url] of fontes) {
    try {
      const txt = await (await fetch(String(url).trim())).text();
      for (const e of lerIcal(txt)) {
        if (e.fim < addDays(today(), -60)) continue;
        lidos++;
        const ja = existentes.find(r => r.c.icalUid === e.uid) ?? novas.find(r => r.c.icalUid === e.uid);
        const bloqueio = /not available|blocked|indispon|closed/i.test(e.resumo);
        const c = { checkin: e.ini, checkout: e.fim, canal, icalUid: e.uid, status: bloqueio ? 'Bloqueio' : 'Confirmada' };
        if (ja) { if (ja.c.checkin !== e.ini || ja.c.checkout !== e.fim) novas.push({ ...ja, c: { ...ja.c, checkin: e.ini, checkout: e.fim } }); continue; }
        novas.push(novoReg('reserva', esp.id, { ...c, hospede: bloqueio ? '' : (e.resumo.replace(/^Reserved\s*-?\s*/i, '') || t('Hóspede ({c})', { c: canal })),
          comissao: COMISSAO_PADRAO[canal] ?? 0, diaria: esp.c.diaria ?? '', taxaLimpeza: esp.c.taxaLimpeza ?? '', notas: e.desc.slice(0, 500) }));
      }
    } catch { falhas++; }
  }
  if (novas.length) {
    salvarVarios(ctx.mod, novas);
    const limpezas = novas.filter(r => r.c.status === 'Confirmada' && !regsDe(ctx.mod).some(x => x.col === 'limpeza' && x.c.reserva === r.id)).map(r => limpezaDa(esp, r));
    salvarVarios(ctx.mod, limpezas);
  }
  Alert.alert(t('Calendários'), falhas && !lidos
    ? (Platform.OS === 'web' ? t('O navegador não deixa ler o calendário das plataformas. Importe pelo app no celular.') : t('Não foi possível ler o calendário. Confira o link em Editar.'))
    : t('{n} reserva(s) nova(s) ou alterada(s).', { n: novas.length }));
}

function limpezaDa(esp: Reg, r: Reg): Reg {
  const l = novoReg('limpeza', esp.id, { data: r.c.checkout, hora: r.c.horaOut || esp.c.horaOut || '11:00', reserva: r.id, valor: esp.c.valorLimpeza ?? '',
    responsavel: esp.c.faxina ?? '', telefone: esp.c.telFaxina ?? '' });
  return { ...l, itens: CHECKLIST.map(text => ({ id: uid(), text: t(text), done: false })) };
}

const mensagemBoasVindas = (esp: Reg, r: Reg) => [
  t('Olá, {nome}! Seja bem-vindo(a).', { nome: r.c.hospede || '' }),
  t('Check-in: {d} a partir das {h}', { d: dataBr(r.c.checkin), h: horaTela(r.c.horaIn || esp.c.horaIn || '14:00') }),
  t('Check-out: {d} até as {h}', { d: dataBr(r.c.checkout), h: horaTela(r.c.horaOut || esp.c.horaOut || '11:00') }),
  esp.c.endereco ? '📍 ' + esp.c.endereco : '',
  esp.c.wifi ? '📶 Wi-Fi: ' + esp.c.wifi : '',
  esp.c.regras ? '\n' + t('Regras da casa:') + '\n' + esp.c.regras : '',
].filter(Boolean).join('\n');

const enviar = (txt: string, titulo: string) => semBloqueio(() => compartilharTexto(txt, titulo)).then(x => { if (x === 'copiado') Alert.alert(t('Texto copiado. Cole onde quiser enviar.')); }).catch(() => {});

const espaco: Colecao = {
  id: 'espaco', nome: N_('Imóveis e espaços'), um: N_('Espaço'), anexos: true, rapido: 'status',
  icone: r => ICONE_STATUS[r.c.status] ?? '🛎️',
  campos: [
    { k: 'nome', r: N_('Nome'), t: 'texto', ph: N_('Ex.: Apartamento da praia, Suíte 1, Edícula') },
    { k: 'modo', r: N_('Aluga como'), t: 'opcao', op: [N_('Imóvel inteiro'), N_('Cômodo ou quarto')] },
    { k: 'status', r: N_('Situação agora'), t: 'opcao', op: STATUS },
    { k: 'endereco', r: N_('Endereço'), t: 'endereco' },
    { k: 'capacidade', r: N_('Hóspedes (máximo)'), t: 'numero' },
    { k: 'diaria', r: N_('Diária padrão'), t: 'dinheiro' },
    { k: 'taxaLimpeza', r: N_('Taxa de limpeza cobrada do hóspede'), t: 'dinheiro' },
    { k: 'horaIn', r: N_('Horário de check-in'), t: 'hora' },
    { k: 'horaOut', r: N_('Horário de check-out'), t: 'hora' },
    { k: 'faxina', r: N_('Quem faz a limpeza'), t: 'texto' },
    { k: 'telFaxina', r: N_('Telefone da limpeza'), t: 'telefone' },
    { k: 'valorLimpeza', r: N_('Quanto paga por limpeza'), t: 'dinheiro' },
    { k: 'wifi', r: N_('Wi-Fi (rede e senha)'), t: 'texto' },
    { k: 'regras', r: N_('Regras da casa'), t: 'longo' },
    { k: 'icalAirbnb', r: N_('Link do calendário do Airbnb (iCal)'), t: 'link' },
    { k: 'icalBooking', r: N_('Link do calendário do Booking (iCal)'), t: 'link' },
    { k: 'icalOutro', r: N_('Link de outro calendário (iCal)'), t: 'link' },
    { k: 'notas', r: N_('Observações'), t: 'longo' },
  ],
  titulo: r => r.c.nome || '',
  sub: (r, ctx) => {
    const n = numerosDoMes(ctx, mesAtual(), [r]);
    return junta(op(r.c.status), op(r.c.modo), n.ocupadas && t('{p}% ocupado no mês', { p: n.ocupacao }), n.bruto > 0 && din(n.lucro, ctx.moeda));
  },
  novo: () => ({ modo: 'Imóvel inteiro', status: 'Livre', horaIn: '14:00', horaOut: '11:00' }),
  acoes: (r, ctx) => [
    { label: '🔄 ' + t('Importar reservas do Airbnb, Booking...'), onPress: () => (r.c.icalAirbnb || r.c.icalBooking || r.c.icalOutro ? importarCalendarios(ctx, r)
      : Alert.alert(t('Sem link de calendário'), t('Em Editar, cole o link do calendário (iCal) do anúncio. No Airbnb: Calendário › Disponibilidade › Conectar calendários › Exportar calendário. No Booking: Tarifas e disponibilidade › Sincronizar calendários.'))) },
    ...(r.c.telFaxina ? [{ label: '💬 ' + t('Falar com a limpeza'), onPress: () => whatsapp(r.c.telFaxina, '') }] : []),
  ],
  painel: PainelEspacos,
  vazio: N_('Cadastre o imóvel inteiro ou cada quarto que você aluga. Dentro de cada um ficam as reservas, a limpeza e as despesas.'),
};

const reserva: Colecao = {
  id: 'reserva', pai: 'espaco', nome: N_('Reservas'), um: N_('Reserva'), data: 'checkin', anexos: true, rapido: 'status',
  icone: r => (r.c.status === 'Cancelada' ? '❌' : r.c.status === 'Bloqueio' ? '⛔' : ICONE_CANAL[r.c.canal] ?? '🛏️'),
  campos: [
    { k: 'hospede', r: N_('Hóspede'), t: 'texto' },
    { k: 'telefone', r: N_('Telefone'), t: 'telefone' },
    { k: 'canal', r: N_('Origem da reserva'), t: 'opcao', op: CANAIS },
    { k: 'status', r: N_('Status'), t: 'opcao', op: [N_('Confirmada'), N_('Pendente'), N_('Concluída'), N_('Cancelada'), N_('Bloqueio')] },
    { k: 'checkin', r: N_('Check-in'), t: 'data', aviso: [1] },
    { k: 'horaIn', r: N_('Hora do check-in'), t: 'hora' },
    { k: 'checkout', r: N_('Check-out'), t: 'data', aviso: [1] },
    { k: 'horaOut', r: N_('Hora do check-out'), t: 'hora' },
    { k: 'pessoas', r: N_('Número de hóspedes'), t: 'numero' },
    { k: 'diaria', r: N_('Diária'), t: 'dinheiro' },
    { k: 'taxaLimpeza', r: N_('Taxa de limpeza'), t: 'dinheiro' },
    { k: 'extras', r: N_('Extras (pet, check-in tardio...)'), t: 'dinheiro' },
    { k: 'comissao', r: N_('Comissão da plataforma'), t: 'numero', un: '%', seg: 'fin' },
    { k: 'total', r: N_('Total bruto'), t: 'dinheiro' },
    { k: 'liquido', r: N_('Você recebe (líquido)'), t: 'dinheiro' },
    { k: 'caucao', r: N_('Caução'), t: 'dinheiro' },
    { k: 'pago', r: N_('Recebido'), t: 'sim', seg: 'fin' },
    { k: 'notas', r: N_('Observações'), t: 'longo' },
  ],
  titulo: r => (r.c.status === 'Bloqueio' ? t('Bloqueado') : r.c.hospede || t('Hóspede')),
  sub: (r, ctx) => junta(r.c.checkin && `${dataBr(r.c.checkin).slice(0, 5)} → ${dataBr(r.c.checkout).slice(0, 5)}`, noites(r.c) && t('{n} noite(s)', { n: noites(r.c) }),
    op(r.c.canal), conta(r) && temNum(r.c.liquido) && din(r.c.liquido, ctx.moeda), r.c.status !== 'Confirmada' && op(r.c.status)),
  feito: r => r.c.status === 'Concluída' || r.c.status === 'Cancelada',
  novo: (ctx, pai) => ({ status: 'Confirmada', canal: 'Direto', checkin: today(), checkout: addDays(today(), 2), diaria: pai?.c.diaria ?? '', taxaLimpeza: pai?.c.taxaLimpeza ?? '',
    horaIn: pai?.c.horaIn ?? '14:00', horaOut: pai?.c.horaOut ?? '11:00', comissao: 0 }),
  ajustar: (c, k) => {
    let x = c;
    if (k === 'canal' && (!temNum(c.comissao) || num(c.comissao) === 0)) x = { ...x, comissao: COMISSAO_PADRAO[c.canal] ?? 0 };
    if (['diaria', 'taxaLimpeza', 'extras', 'comissao', 'checkin', 'checkout', 'canal'].includes(k)) {
      const v = calcReserva(x);
      x = { ...x, total: Math.round(v.bruto * 100) / 100, liquido: Math.round(v.liquido * 100) / 100 };
    }
    return x;
  },
  aoSalvar: (r, ctx) => {
    // limpeza criada sozinha no dia do check-out
    if (!conta(r) || !r.c.checkout) return;
    const esp = ctx.porId(r.pai);
    if (!esp || regsDe(ctx.mod).some(x => x.col === 'limpeza' && x.c.reserva === r.id)) return;
    salvarVarios(ctx.mod, [limpezaDa(esp, r)]);
  },
  agenda: (r, ctx) => { const esp = ctx.porId(r.pai); return r.c.checkin ? { start: r.c.checkin, time: r.c.horaIn || null, title: t('Check-in: {h} · {e}', { h: r.c.hospede || '', e: esp?.c.nome ?? '' }) } : null; },
  acoes: (r, ctx) => { const esp = ctx.porId(r.pai); return esp ? [
    ...(r.c.telefone ? [{ label: '💬 ' + t('Boas-vindas pelo WhatsApp'), onPress: () => whatsapp(r.c.telefone, mensagemBoasVindas(esp, r)) }] : []),
    { label: '📤 ' + t('Enviar instruções ao hóspede'), onPress: () => enviar(mensagemBoasVindas(esp, r), esp.c.nome) },
    ...(conta(r) && !r.c.pago ? [{ label: '💰 ' + t('Marcar como recebido'), onPress: () => mudarCampos(ctx.mod, r.id, { pago: true }) }] : []),
  ] : []; },
  filtros: [[N_('Próximas'), r => conta(r) && r.c.checkout >= today()], [N_('Hospedados agora'), r => conta(r) && r.c.checkin <= today() && r.c.checkout > today()],
    [N_('A receber'), r => conta(r) && !r.c.pago], [N_('Canceladas'), r => r.c.status === 'Cancelada']],
  painel: PainelReservas,
  vazio: N_('Anote cada reserva com as datas, a origem e os valores. A limpeza do check-out entra sozinha na lista de limpezas.'),
};

const limpeza: Colecao = {
  id: 'limpeza', pai: 'espaco', nome: N_('Limpezas'), um: N_('Limpeza'), data: 'data', check: 'feito', anexos: true, itens: N_('Checklist'),
  marcas: [N_('Antes'), N_('Depois')],
  icone: r => (r.c.feito ? '✨' : '🧹'),
  campos: [
    { k: 'data', r: N_('Dia'), t: 'data', aviso: [1, 0] },
    { k: 'hora', r: N_('Horário'), t: 'hora' },
    { k: 'responsavel', r: N_('Quem vai limpar'), t: 'texto' },
    { k: 'telefone', r: N_('Telefone'), t: 'telefone' },
    { k: 'valor', r: N_('Valor a pagar'), t: 'dinheiro' },
    { k: 'pagoFaxina', r: N_('Pago'), t: 'sim', seg: 'fin' },
    { k: 'feito', r: N_('Pronto'), t: 'sim' },
    { k: 'notas', r: N_('Observações'), t: 'longo' },
  ],
  titulo: (r, ctx) => { const res = ctx.porId(r.c.reserva); return res ? t('Limpeza após {h}', { h: res.c.hospede || t('a reserva') }) : t('Limpeza'); },
  sub: (r, ctx) => junta(dataBr(r.c.data), horaTela(r.c.hora), r.c.responsavel, temNum(r.c.valor) && din(r.c.valor, ctx.moeda), r.c.pagoFaxina && t('pago')),
  feito: r => !!r.c.feito,
  novo: (ctx, pai) => ({ data: today(), hora: pai?.c.horaOut ?? '11:00', responsavel: pai?.c.faxina ?? '', telefone: pai?.c.telFaxina ?? '', valor: pai?.c.valorLimpeza ?? '' }),
  aoSalvar: (r, ctx) => { if (r.c.feito) { const esp = ctx.porId(r.pai); if (esp?.c.status === 'Em limpeza') mudarCampos(ctx.mod, esp.id, { status: 'Livre' }); } },
  agenda: (r, ctx) => ({ start: r.c.data, time: r.c.hora || null, title: t('Limpeza · {e}', { e: ctx.porId(r.pai)?.c.nome ?? '' }) }),
  acoes: (r, ctx) => {
    const esp = ctx.porId(r.pai);
    const txt = [t('Limpeza: {e}', { e: esp?.c.nome ?? '' }), `📅 ${dataBr(r.c.data)}${r.c.hora ? ' · ' + horaTela(r.c.hora) : ''}`, esp?.c.endereco ? '📍 ' + esp.c.endereco : '',
      r.itens?.length ? '\n' + r.itens.map(i => `☐ ${i.text}`).join('\n') : '', r.c.notas ? '\n' + r.c.notas : ''].filter(Boolean).join('\n');
    return [
      ...(r.c.telefone ? [{ label: '💬 ' + t('Mandar para quem limpa (WhatsApp)'), onPress: () => whatsapp(r.c.telefone, txt) }] : []),
      { label: '📤 ' + t('Compartilhar a limpeza'), onPress: () => enviar(txt, t('Limpeza')) },
      ...(esp?.c.endereco ? [{ label: '🗺️ ' + t('Como chegar'), onPress: () => abrirRota(esp.c.endereco) }] : []),
    ];
  },
  filtros: [[N_('A fazer'), r => !r.c.feito], [N_('Feitas'), r => !!r.c.feito], [N_('A pagar'), r => !r.c.pagoFaxina && temNum(r.c.valor)]],
  vazio: N_('As limpezas dos check-outs aparecem aqui sozinhas, com checklist e fotos de antes e depois.'),
};

const despesa: Colecao = {
  id: 'despesa', pai: 'espaco', nome: N_('Despesas'), um: N_('Despesa'), data: 'data', ordem: 'data-desc', anexos: true, check: 'pago',
  pagamento: { tipo: 'pagar', venc: 'data', data: 'pagoEm', valorPago: 'valorPago' },
  icone: '🧾',
  campos: [
    { k: 'descricao', r: N_('Descrição'), t: 'texto' },
    { k: 'categoria', r: N_('Categoria'), t: 'opcao', op: [N_('Lavanderia'), N_('Insumos (papel, sabonete, café)'), N_('Manutenção'), N_('Luz, água, gás e internet'), N_('Condomínio e IPTU'), N_('Taxas e anúncios'), N_('Móveis e enxoval'), N_('Outro')] },
    { k: 'data', r: N_('Data'), t: 'data' },
    { k: 'valor', r: N_('Valor'), t: 'dinheiro' },
    { k: 'pago', r: N_('Pago'), t: 'sim', seg: 'fin' },
    { k: 'pagoEm', r: N_('Data do pagamento'), t: 'data', mostrar: c => !!c.pago, seg: 'fin' },
    { k: 'valorPago', r: N_('Valor pago'), t: 'dinheiro', mostrar: c => !!c.pago },
    { k: 'notas', r: N_('Observações'), t: 'longo' },
  ],
  titulo: r => r.c.descricao || op(r.c.categoria),
  sub: (r, ctx) => junta(dataBr(r.c.data), op(r.c.categoria), dinDe(r, 'valor', ctx.moeda)),
  novo: () => ({ data: today(), pago: true, categoria: 'Insumos (papel, sabonete, café)' }),
  resumo: (l, ctx) => (l.length ? t('Total: {v}', { v: dinSomas(somaPorMoeda(l, r => num(r.c.valor), ctx.moeda), ctx.moeda) }) : null),
  vazio: N_('Lavanderia, insumos, manutenção, contas e taxas. Entram no lucro do mês.'),
};

export const HOSPEDAGEM: ModDef = { id: 'hospedagem', colecoes: [espaco, reserva, limpeza, despesa],
  dica: N_('Para quem aluga o imóvel ou um cômodo por temporada (Airbnb, Booking ou direto): reservas, limpeza, despesas e o lucro de cada mês.') };
